import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Member,
  changeMemberPassword,
  forgotMemberPassword,
  getCurrentMember,
  loginMember,
  logoutMember,
  registerMember,
  resetMemberPassword,
  updateMemberProfile,
  type RegisterPayload,
  type UpdateMemberProfilePayload,
} from "@/services/memberAuth";

type MemberAuthContextValue = {
  member: Member | null;
  isLoggedIn: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<Member>;
  register: (payload: RegisterPayload) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  updateProfile: (payload: UpdateMemberProfilePayload) => Promise<Member>;
  changePassword: (payload: { CurrentPassword: string; NewPassword: string }) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<Member>;
};

const MemberAuthContext = createContext<MemberAuthContextValue | null>(null);

export function MemberAuthProvider({ children }: { children: ReactNode }) {
  // 後台管理（/admin/*）與門市端（/store/*）使用各自獨立的工作區 Session，跟會員登入系統無關；
  // 這些頁面本來就不會有會員登入狀態，這裡直接跳過會員狀態請求，避免多餘的 session 與網路往返。
  const location = useLocation();
  const isWorkspaceRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/store");

  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(!isWorkspaceRoute);

  const refresh = useCallback(async () => {
    try {
      const data = await getCurrentMember();
      setMember(data.Member);
    } catch {
      setMember(null);
    }
  }, []);

  useEffect(() => {
    if (isWorkspaceRoute) {
      setMember(null);
      setLoading(false);
      return;
    }

    let alive = true;
    (async () => {
      try {
        const data = await getCurrentMember();
        if (alive) setMember(data.Member);
      } catch {
        if (alive) setMember(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isWorkspaceRoute]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await loginMember(email, password);
    setMember(data.Member);
    return data.Member;
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    await registerMember(payload);
  }, []);

  const logout = useCallback(async () => {
    await logoutMember().catch(() => undefined);
    setMember(null);
  }, []);

  const updateProfile = useCallback(async (payload: UpdateMemberProfilePayload) => {
    const data = await updateMemberProfile(payload);
    setMember(data.Member);
    return data.Member;
  }, []);

  const changePassword = useCallback(async (payload: { CurrentPassword: string; NewPassword: string }) => {
    await changeMemberPassword(payload);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await forgotMemberPassword(email);
  }, []);

  const resetPassword = useCallback(async (token: string, newPassword: string) => {
    const data = await resetMemberPassword(token, newPassword);
    setMember(data.Member);
    return data.Member;
  }, []);

  const value = useMemo<MemberAuthContextValue>(() => ({
    member,
    isLoggedIn: member !== null,
    loading,
    login,
    register,
    logout,
    refresh,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
  }), [member, loading, login, register, logout, refresh, updateProfile, changePassword, forgotPassword, resetPassword]);

  return <MemberAuthContext.Provider value={value}>{children}</MemberAuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMemberAuth() {
  const context = useContext(MemberAuthContext);
  if (!context) {
    throw new Error("useMemberAuth must be used within a MemberAuthProvider");
  }
  return context;
}
